import React from 'react';
import type { RootState } from '@/store';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import CalculateIcon from '@mui/icons-material/Calculate';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';

import {
  USER_APPROVE_STATUS,
  USER_LOAN_PROGRAM,
  USER_QUALIFY_STATUS,
  USER_ROLE,
} from '@/config/constants';
import type { FeeItem } from '@/apis/extended/admin/interestRateApi';
import { CollapsibleSection } from '@/components/Common/CollapsibleSection';
import { GlassCard } from '@/components/ui/GlassCard';
import { useEstimation } from '@/hooks/useEstimation';
import { useProductStatus } from '@/hooks/useProductStatus';
import {
  computeMergedFeeBreakdown,
  type CalculatedFeeRule,
} from '@/utils/fees/feeEngine';
import {
  Product,
  PRODUCT_APPROVE_STATUS,
  PRODUCT_QUALIFY_STATUS,
} from '@/types/ProductType';
import {
  ProductDetailCashToClose,
  productDetailCashToCloseHasContent,
} from './ProductDetailCards/ProductDetailCashToClose';
import { ProductDetailClosingCosts } from './ProductDetailCards/ProductDetailClosingCosts';
import { ProductDetailLoanCosts } from './ProductDetailCards/ProductDetailLoanCosts';
import { ProductDetailLoanTerms } from './ProductDetailCards/ProductDetailLoanTerms';
import { ProductDetailOtherCosts } from './ProductDetailCards/ProductDetailOtherCosts';
import { ProductDetailProfitability } from './ProductDetailCards/ProductDetailProfitability';
import { ProductDetailProjectedIncome } from './ProductDetailCards/ProductDetailProjectedIncome';
import { ProductDetailSubtotalClosingCosts } from './ProductDetailCards/ProductDetailSubtotalClosingCosts';
import { ProductDetailNoEstimation } from './ProductDetailNoEstimation';

interface Comparison {
  loanProgram: USER_LOAN_PROGRAM;
  downPayment: number;
  bridgeTermMonths?: number;
}

interface ProductDetailEstimationProps {
  product: Product;
  comparisons?: Comparison[];
  onBridgeTermMonthsChange?: (comparisonIndex: number, months: number) => void;
}

// Separate component for each estimation column to properly use hooks
const EstimationColumn = ({
  product,
  comp,
  comparisonIndex,
  showBorder,
  isFirstColumn,
  totalColumns,
  onBridgeTermMonthsChange,
}: {
  product: Product;
  comp: Comparison;
  comparisonIndex: number;
  showBorder: boolean;
  isFirstColumn: boolean;
  totalColumns: number;
  onBridgeTermMonthsChange?: (comparisonIndex: number, months: number) => void;
}) => {
  const theme = useTheme();
  const [localBridgeMonths, setLocalBridgeMonths] = React.useState(12);
  React.useEffect(() => {
    if (comp.loanProgram !== USER_LOAN_PROGRAM.BRIDGE) {
      setLocalBridgeMonths(12);
    }
  }, [comp.loanProgram]);

  const bridgeControlled = Boolean(onBridgeTermMonthsChange);
  const bridgeTermForEstimation =
    comp.loanProgram === USER_LOAN_PROGRAM.BRIDGE
      ? bridgeControlled
        ? comp.bridgeTermMonths ?? 12
        : localBridgeMonths
      : undefined;

  const setBridgeTermMonths = (months: number) => {
    if (bridgeControlled) {
      onBridgeTermMonthsChange!(comparisonIndex, months);
    } else {
      setLocalBridgeMonths(months);
    }
  };

  const { estimation } = useEstimation(
    product,
    comp.downPayment,
    comp.loanProgram,
    bridgeTermForEstimation
  );
  const { approveStatus, qualifyStatus } = useProductStatus(product);
  const isUserApproved =
    product.user?.approveStatus === USER_APPROVE_STATUS.APPROVED &&
    Boolean(product.user?.approvalAmount);
  const isUserQualified =
    product.user?.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY;
  /** Profile-level approval qualifies for full estimate; property-level gates are for badges only. */
  const isLimited =
    !isUserApproved &&
    !isUserQualified &&
    approveStatus !== PRODUCT_APPROVE_STATUS.APPROVED &&
    qualifyStatus !== PRODUCT_QUALIFY_STATUS.QUALIFIED;
  const feeItemsByLoanType = useSelector(
    (state: RootState) => (state.interestRate as any).feeItemsByLoanType
  ) as Partial<Record<string, FeeItem[]>>;
  const calculatedFeeRulesByLoanType = useSelector(
    (state: RootState) =>
      (state.interestRate as any).calculatedFeeRulesByLoanType
  ) as Partial<Record<string, CalculatedFeeRule[]>>;

  if (!estimation) return <ProductDetailNoEstimation product={product} />;

  const getLoanProgramColor = (program: USER_LOAN_PROGRAM) => {
    switch (program) {
      case USER_LOAN_PROGRAM.CONVENTIONAL:
        return theme.palette.primary.main;
      case USER_LOAN_PROGRAM.FHA:
        return '#4caf50';
      case USER_LOAN_PROGRAM.VA:
        return '#ff9800';
      case USER_LOAN_PROGRAM.USDA:
        return '#9c27b0';
      case USER_LOAN_PROGRAM.DSCR:
        return '#f44336';
      case USER_LOAN_PROGRAM.COMMERCIAL:
        return '#e91e63';
      case USER_LOAN_PROGRAM.FIX_AND_FLIP:
        return '#795548';
      default:
        return '#757575';
    }
  };

  const getStatusColor = () => {
    if (isLimited) return 'warning';
    return 'success';
  };

  const programColor = getLoanProgramColor(comp.loanProgram);

  const loanTypeMap: Record<string, string> = {
    conventional: 'Conventional',
    fha: 'FHA',
    va: 'VA',
    usda: 'USDA',
    dscr: 'DSCR',
    commercial: 'Commercial',
    fix_and_flip: 'FixAndFlip',
    bridge: 'Bridge',
    heloc: 'HELOC',
    heloan: 'HELOAN',
    second_mortgage: 'SecondMortgage',
    cash: 'Cash',
    default: 'Conventional',
  };
  const loanType =
    loanTypeMap[((estimation as any).type || '').toLowerCase()] ||
    'Conventional';
  const feeItems = feeItemsByLoanType?.[loanType] || [];
  const calculatedFeeRules = calculatedFeeRulesByLoanType?.[loanType] || [];
  const feeBreakdown = computeMergedFeeBreakdown({
    estimation,
    calculatedFeeRules,
    feeItems,
    loanAmount: (estimation as any).loanAmount || 0,
  });
  const totalClosingCosts = feeBreakdown.totalClosingCosts;

  return (
    <GlassCard
      variant="medium"
      sx={{
        flex: 1,
        width: '100%',
        border: showBorder
          ? `3px solid ${programColor}`
          : `2px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        borderRadius: 4,
        position: 'relative',
        overflow: 'hidden',
        minHeight: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&:hover': {
          transform: 'translateY(-4px)',
        },
        '&::before': showBorder
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              background: `linear-gradient(135deg, ${programColor} 0%, ${alpha(
                programColor,
                0.7
              )} 100%)`,
              borderRadius: '4px 4px 0 0',
            }
          : {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${
                theme.palette.primary.main
              } 0%, ${alpha(theme.palette.primary.main, 0.35)} 100%)`,
              borderRadius: '3px 3px 0 0',
            },
      }}
    >
      <CardContent
        sx={{
          p: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
          pt: showBorder
            ? { xs: 2.5, sm: 3, md: 3.5, lg: 4 }
            : { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Enhanced Header */}

        {/* Key Metrics Highlight Section */}
        {!isLimited && (
          <Box
            sx={{
              mb: { xs: 2, sm: 2.5, md: 3 },
              p: { xs: 1.5, sm: 2, md: 2.5 },
              //background: `black`,
              borderRadius: 3,
              border: `1px solid ${theme.palette.greyShades.border1}`,
              width: '100%',
            }}
          >
            <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
              <Grid item xs={6}>
                <Box
                  sx={{
                    textAlign: 'center',
                    background: alpha(theme.palette.primary.main, 0.04),
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.greyShades.border1}`,
                    p: { xs: 1.5, sm: 1.75, md: 2 },
                  }}
                >
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    Monthly Payment
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{
                      color: theme.palette.text.primary,
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    }}
                  >
                    $
                    {(estimation as any).cleanPayment?.toLocaleString() ||
                      'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    textAlign: 'center',
                    background: alpha(theme.palette.success.main, 0.06),
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.greyShades.border1}`,
                    p: { xs: 1.5, sm: 1.75, md: 2 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-150%',
                      width: '50%',
                      height: '100%',
                      background: `linear-gradient(120deg, transparent, ${alpha(
                        theme.palette.success.main,
                        0.15
                      )}, transparent)`,
                      animation: 'unbeatableShimmer 7.5s ease-in-out infinite',
                      pointerEvents: 'none',
                    },
                    '@keyframes unbeatableShimmer': {
                      '0%': { transform: 'translateX(0)' },
                      '25%': { transform: 'translateX(350%)' },
                      '100%': { transform: 'translateX(350%)' },
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    display="block"
                    fontSize={{ xs: '0.875rem', sm: '1rem' }}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Unbeatable Savings
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{
                      color: theme.palette.success.main,
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    }}
                  >
                    $
                    {(
                      estimation.cashToClose -
                      (estimation as any).unbeatableDeal
                    ).toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Loan Terms Section - Expanded by default */}
        {!isLimited && (
          <Box
            sx={{
              width: '100%',
              overflow: 'hidden',
              mb: { xs: 1.5, sm: 2, md: 2.5 },
            }}
          >
            <GlassCard variant="subtle" sx={{ width: '100%' }}>
              <CollapsibleSection
                title="Loan Terms"
                color={theme.palette.primary.main}
                icon={
                  <AccountBalanceIcon
                    sx={{ color: theme.palette.primary.main }}
                  />
                }
                totalValue={estimation.loanAmount}
                totalLabel="Loan Amount"
                defaultExpanded={true}
                sx={{ width: '100%' }}
              >
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                  <ProductDetailLoanTerms
                    estimation={estimation}
                    product={product}
                    bridgeTermMonths={
                      estimation.type === USER_LOAN_PROGRAM.BRIDGE
                        ? bridgeTermForEstimation
                        : undefined
                    }
                    onBridgeTermMonthsChange={
                      estimation.type === USER_LOAN_PROGRAM.BRIDGE
                        ? setBridgeTermMonths
                        : undefined
                    }
                    onUpdate={() => {
                      // Force re-render by updating the component
                      window.location.reload();
                    }}
                  />
                </Box>
              </CollapsibleSection>
            </GlassCard>
          </Box>
        )}

        {/* Profitability Section - Collapsed by default */}
        {!isLimited &&
          estimation &&
          (estimation.type === USER_LOAN_PROGRAM.DSCR ||
            estimation.type === USER_LOAN_PROGRAM.COMMERCIAL ||
            estimation.type === USER_LOAN_PROGRAM.FIX_AND_FLIP ||
            estimation.type === USER_LOAN_PROGRAM.BRIDGE) && (
            <Box
              sx={{
                width: '100%',
                overflow: 'hidden',
                mb: { xs: 1.5, sm: 2, md: 2.5 },
              }}
            >
              <GlassCard variant="subtle" sx={{ width: '100%' }}>
                <CollapsibleSection
                  title="Profitability Analysis"
                  color={theme.palette.primary.main}
                  icon={
                    <TrendingUpIcon
                      sx={{ color: theme.palette.primary.main }}
                    />
                  }
                  totalValue={undefined}
                  totalLabel={undefined}
                  defaultExpanded={false}
                  sx={{ width: '100%' }}
                >
                  <Box sx={{ width: '100%', overflow: 'hidden' }}>
                    <ProductDetailProfitability
                      estimation={estimation}
                      product={product}
                    />
                  </Box>
                </CollapsibleSection>
              </GlassCard>
            </Box>
          )}

        {/* DSCR/Commercial Specific Section - Collapsed by default */}
        {!isLimited &&
          estimation &&
          (estimation.type === USER_LOAN_PROGRAM.DSCR ||
            estimation.type === USER_LOAN_PROGRAM.COMMERCIAL) && (
            <Box
              sx={{
                width: '100%',
                overflow: 'hidden',
                mb: { xs: 1.5, sm: 2, md: 2.5 },
              }}
            >
              <CollapsibleSection
                title="Projected Cashflow"
                color={theme.palette.primary.main}
                icon={
                  <BusinessIcon sx={{ color: theme.palette.primary.main }} />
                }
                totalValue={
                  estimation.type === USER_LOAN_PROGRAM.DSCR
                    ? (estimation as any).dscr
                    : (estimation as any).cashFlow
                }
                totalLabel={
                  estimation.type === USER_LOAN_PROGRAM.DSCR
                    ? 'DSCR'
                    : 'Monthly Cash Flow'
                }
                defaultExpanded={false}
                sx={{ width: '100%' }}
              >
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                  <ProductDetailProjectedIncome estimation={estimation} />
                </Box>
              </CollapsibleSection>
            </Box>
          )}

        {/* Closing Costs Section - Collapsed by default */}
        {!isLimited && (
          <Box
            sx={{
              width: '100%',
              overflow: 'hidden',
              mb: { xs: 1.5, sm: 2, md: 2.5 },
            }}
          >
            <CollapsibleSection
              title="Closing Costs Breakdown"
              color={theme.palette.primary.main}
              icon={<ReceiptIcon sx={{ color: theme.palette.primary.main }} />}
              totalValue={totalClosingCosts}
              totalLabel="Total Closing Costs"
              defaultExpanded
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%', overflow: 'hidden' }}>
                <ProductDetailClosingCosts estimation={estimation} />
              </Box>
            </CollapsibleSection>
          </Box>
        )}

        {/* Other Costs Section - Collapsed by default */}
        {!isLimited && (
          <Box
            sx={{
              width: '100%',
              overflow: 'hidden',
              mb: { xs: 1.5, sm: 2, md: 2.5 },
            }}
          >
            <CollapsibleSection
              title="Costs Paid before Closing"
              color={theme.palette.primary.main}
              icon={
                <AttachMoneyIcon sx={{ color: theme.palette.primary.main }} />
              }
              totalValue={(estimation as any).cashOutsideClosing}
              totalLabel="Total Costs"
              defaultExpanded={false}
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%', overflow: 'hidden' }}>
                <ProductDetailOtherCosts estimation={estimation} />
              </Box>
            </CollapsibleSection>
          </Box>
        )}

        {/* Subtotal Closing Costs Section - Collapsed by default */}
        {!isLimited && (
          <Box
            sx={{
              width: '100%',
              overflow: 'hidden',
              mb: { xs: 1.5, sm: 2, md: 2.5 },
            }}
          >
            <CollapsibleSection
              title="Subtotals of Closing Costs"
              color={theme.palette.primary.main}
              icon={
                <CalculateIcon sx={{ color: theme.palette.primary.main }} />
              }
              totalValue={totalClosingCosts}
              totalLabel="Total Closing Costs"
              defaultExpanded
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%', overflow: 'hidden' }}>
                <ProductDetailSubtotalClosingCosts estimation={estimation} />
              </Box>
            </CollapsibleSection>
          </Box>
        )}

        {productDetailCashToCloseHasContent(estimation) && (
          <Box
            sx={{
              mt: { xs: 2, sm: 2.5, md: 3 },
              pt: { xs: 2, sm: 2.5, md: 3 },
              borderTop: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              background: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 3,
              p: { xs: 2, sm: 3 },
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -3,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${
                  theme.palette.primary.main
                } 0%, ${alpha(theme.palette.primary.main, 0.35)} 100%)`,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <ProductDetailCashToClose estimation={estimation} />
          </Box>
        )}
      </CardContent>
    </GlassCard>
  );
};

export const ProductDetailEstimation = ({
  product,
  comparisons,
  onBridgeTermMonthsChange,
}: ProductDetailEstimationProps) => {
  const theme = useTheme();

  // If comparisons not provided, use product's current values
  const fbProgram = product.user?.loanProgram || USER_LOAN_PROGRAM.CONVENTIONAL;
  const fallbackComparisons: Comparison[] = [
    {
      loanProgram: fbProgram,
      downPayment: product.user?.loanInfo?.downPayment || 0,
      ...(fbProgram === USER_LOAN_PROGRAM.BRIDGE
        ? { bridgeTermMonths: 12 }
        : {}),
    },
  ];
  const comps =
    comparisons && comparisons.length > 0 ? comparisons : fallbackComparisons;

  // Create estimation products for each comparison
  const estimationProducts = comps.map((comp) => ({
    ...product,
    user: {
      ...product.user,
      _id: product.user?._id || '',
      name: product.user?.name || '',
      username: product.user?.username || '',
      email: product.user?.email || '',
      phone: product.user?.phone || '',
      role: product.user?.role || USER_ROLE.USER,
      approveStatus:
        product.user?.approveStatus || USER_APPROVE_STATUS.NOT_SUBMITTED,
      qualifyStatus: product.user?.qualifyStatus || USER_QUALIFY_STATUS.INITIAL,
      securityEnabled: product.user?.securityEnabled ?? false,
      loanProgram: comp.loanProgram,
      loanInfo: {
        monthlyIncome: product.user?.loanInfo?.monthlyIncome ?? 0,
        creditScore: product.user?.loanInfo?.creditScore ?? 0,
        downPayment: comp.downPayment,
        monthlyDebt: product.user?.loanInfo?.monthlyDebt ?? 0,
        loanTerm: product.user?.loanInfo?.loanTerm ?? 0,
      },
    },
    // Keep the product's loanInfo separate (contains zipCode, offerPrice, hoaFees, etc.)
    loanInfo: {
      zipCode: product.loanInfo?.zipCode ?? 0,
      offerPrice: product.loanInfo?.offerPrice ?? 0,
      hoaFees: product.loanInfo?.hoaFees ?? 0,
      commission: product.loanInfo?.commission ?? 0,
      rent: product.loanInfo?.rent ?? 0,
      experience: product.loanInfo?.experience ?? 0,
      rehab: product.loanInfo?.rehab ?? 0,
      arv: product.loanInfo?.arv ?? 0,
    },
  })) as Product[];

  return (
    <Grid
      container
      spacing={{ xs: 1.5, sm: 2, md: 3, lg: 4 }}
      sx={{
        minHeight: 'fit-content',
        width: '100%',
        margin: 0,
      }}
    >
      {estimationProducts.map((estProduct, idx) => (
        <Grid
          item
          xs={12}
          sm={estimationProducts.length === 1 ? 12 : 6}
          md={
            estimationProducts.length === 1
              ? 12
              : 12 / estimationProducts.length
          }
          lg={
            estimationProducts.length === 1
              ? 12
              : 12 / estimationProducts.length
          }
          xl={
            estimationProducts.length === 1
              ? 12
              : 12 / estimationProducts.length
          }
          key={idx}
          sx={{
            display: 'flex',
            '& > *': { width: '100%' },
          }}
        >
          <EstimationColumn
            product={estProduct}
            comp={comps[idx]}
            comparisonIndex={idx}
            showBorder={estimationProducts.length > 1}
            isFirstColumn={idx === 0}
            totalColumns={estimationProducts.length}
            onBridgeTermMonthsChange={onBridgeTermMonthsChange}
          />
        </Grid>
      ))}
    </Grid>
  );
};
